import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import axiosInstance from '@/lib/axios';
import { toast } from 'sonner';
import {
  Plus,
  Edit2,
  Trash2,
  Search,
  Loader2,
  Clock,
  MessageSquare,
  Heart,
  Image as ImageIcon,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { ImageFileInput } from '@/components/forms/image-file-input';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import { getInformativePostLabelClasses } from '@/lib/informative-post-label-styles';

const INFORMATIVE_LABELS = [
  'Thought of the day',
  'Health Tip',
  'Do you know?',
  "Today's wisdom",
  'Motivation',
  'Tip of the day',
  'Quick reminder',
  'Custom',
];

const informativePostSchema = z.object({
  label: z.string().min(1, 'Label is required'),
  content: z.string().min(1, 'Content is required'),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
});

type InformativePostFormValues = z.infer<typeof informativePostSchema>;

interface InformativePost {
  id: string;
  label: string;
  content: string;
  image: string | null;
  startDate: string;
  endDate: string | null;
  createdAt: string;
  updatedAt: string;
  authorId: string;
  authorFirstName: string | null;
  authorLastName: string | null;
  authorImage: string | null;
  likeCount: number;
  commentCount: number;
  isLiked: boolean;
}

export default function InformativePostsPage() {
  const { communityId } = useParams();
  const [posts, setPosts] = useState<InformativePost[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<InformativePost | null>(null);
  const [search, setSearch] = useState('');
  const [customLabel, setCustomLabel] = useState('');
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [preparingImage, setPreparingImage] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<InformativePostFormValues>({
    resolver: zodResolver(informativePostSchema),
    defaultValues: {
      label: '',
      content: '',
      startDate: '',
      endDate: '',
    },
  });

  const fetchPosts = useCallback(async () => {
    if (!communityId) return;
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/api/communities/${communityId}/informative-posts`);
      setPosts(response.data.posts);
    } catch (_error) {
      toast.error('Failed to fetch informative posts');
    } finally {
      setLoading(false);
    }
  }, [communityId]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  useEffect(() => {
    if (editingPost) {
      form.reset({
        label: editingPost.label,
        content: editingPost.content,
        startDate: editingPost.startDate
          ? new Date(editingPost.startDate).toISOString().slice(0, 16)
          : '',
        endDate: editingPost.endDate
          ? new Date(editingPost.endDate).toISOString().slice(0, 16)
          : '',
      });
      setImagePreview(editingPost.image);
      setImageFile(null);
    } else {
      form.reset({
        label: '',
        content: '',
        startDate: '',
        endDate: '',
      });
      setImagePreview(null);
      setImageFile(null);
      setCustomLabel('');
    }
  }, [editingPost, form]);

  const onSubmit = async (values: InformativePostFormValues) => {
    if (!communityId) return;
    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('label', values.label === 'Custom' ? customLabel : values.label);
      formData.append('content', values.content);
      if (values.startDate) {
        formData.append('startDate', new Date(values.startDate).toISOString());
      }
      if (values.endDate) {
        formData.append('endDate', new Date(values.endDate).toISOString());
      }

      if (imageFile) {
        formData.append('image', imageFile);
      }

      await axiosInstance.post(`/api/communities/${communityId}/informative-posts`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Informative post created successfully');
      setDialogOpen(false);
      setEditingPost(null);
      fetchPosts();
    } catch (_error) {
      toast.error('Failed to save informative post');
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this informative post?')) return;
    try {
      await axiosInstance.delete(`/api/informative-posts/${id}`);
      toast.success('Informative post deleted successfully');
      fetchPosts();
    } catch (_error) {
      toast.error('Failed to delete informative post');
    }
  };

  const handleLabelChange = (value: string) => {
    form.setValue('label', value);
    if (value !== 'Custom') {
      setCustomLabel('');
    }
  };

  const filteredPosts = posts.filter(
    (p) =>
      p.label.toLowerCase().includes(search.toLowerCase()) ||
      p.content.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 md:p-8 space-y-6 md:space-y-8 pb-10 max-w-6xl mx-auto">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Informative Posts</h1>
          <p className="text-muted-foreground mt-2 max-w-xl">
            Manage posts shown in the Featured section at the top of the community social feed.
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button
            className="rounded-xl font-semibold shadow-sm"
            onClick={() => {
              setEditingPost(null);
              setDialogOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" /> New Post
          </Button>
        </div>
      </div>

      <Card className="glass-card border-border/70 shadow-lg overflow-hidden">
        <CardHeader className="space-y-0 pb-4">
          <Input
            placeholder="Search posts..."
            icon={<Search className="h-4 w-4" />}
            uiSize="lg"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-xl"
          />
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-10 w-10 animate-spin text-primary opacity-50" />
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-14 px-4 rounded-2xl border border-dashed border-border/70 bg-muted/20">
              <div className="mx-auto w-14 h-14 rounded-2xl bg-muted/60 flex items-center justify-center mb-4">
                <MessageSquare className="h-7 w-7 text-muted-foreground" />
              </div>
              <p className="font-semibold text-foreground">No informative posts yet</p>
              <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
                Create your first informative post to engage your community.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredPosts.map((post) => (
                <div
                  key={post.id}
                  className="group relative p-4 md:p-5 rounded-2xl border border-border bg-card/60 hover:bg-muted/25 dark:hover:bg-muted/15 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <Badge
                          variant="outline"
                          className={cn(
                            'text-[11px] font-semibold',
                            getInformativePostLabelClasses(post.label)
                          )}
                        >
                          {post.label}
                        </Badge>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3 shrink-0" />
                          {formatDistanceToNow(new Date(post.createdAt), {
                            addSuffix: true,
                          })}
                        </span>
                      </div>
                      <p className="text-sm text-foreground/90 line-clamp-3 whitespace-pre-wrap">
                        {post.content.replace(/<[^>]*>/g, '')}
                      </p>
                      {post.image && (
                        <img
                          src={post.image}
                          alt=""
                          className="mt-3 rounded-xl max-h-48 object-cover border border-border/60"
                        />
                      )}
                      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Heart className="h-3 w-3" />
                          {post.likeCount}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageSquare className="h-3 w-3" />
                          {post.commentCount}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 rounded-xl text-muted-foreground hover:text-primary hover:bg-primary/10"
                        onClick={() => {
                          setEditingPost(post);
                          setDialogOpen(true);
                        }}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 rounded-xl text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => onDelete(post.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto border-border">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {editingPost ? 'Edit Informative Post' : 'Create Informative Post'}
            </DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 py-4">
              <FormField
                control={form.control}
                name="label"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground">Label</FormLabel>
                    <Select value={field.value} onValueChange={handleLabelChange}>
                      <FormControl>
                        <SelectTrigger className="h-12 bg-background/50 border-input focus:border-primary/50">
                          <SelectValue placeholder="Select a label" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {INFORMATIVE_LABELS.map((label) => (
                          <SelectItem key={label} value={label}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {form.watch('label') === 'Custom' && (
                <div className="space-y-2">
                  <Label className="text-foreground">Custom Label</Label>
                  <Input
                    placeholder="Enter your custom label"
                    value={customLabel}
                    onChange={(e) => setCustomLabel(e.target.value)}
                    className="h-12 bg-background/50 border-input focus:border-primary/50"
                  />
                </div>
              )}
              <FormField
                control={form.control}
                name="content"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground">Content</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Write your message here..."
                        className="min-h-[120px] bg-background/50 border-input focus:border-primary/50"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="space-y-2">
                <Label className="text-foreground">Image (Optional)</Label>
                <div className="flex items-center gap-4">
                  <ImageFileInput
                    id="image-input"
                    className="hidden"
                    accept="image/*"
                    onPreparingChange={setPreparingImage}
                    onPrepared={setImageFile}
                    onPreparedDataUrl={setImagePreview}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('image-input')?.click()}
                    className="h-12"
                    disabled={preparingImage}
                  >
                    {preparingImage ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <ImageIcon className="mr-2 h-4 w-4" />
                    )}
                    {preparingImage ? 'Preparing…' : 'Upload Image'}
                  </Button>
                  {imagePreview && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        setImagePreview(null);
                        setImageFile(null);
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </div>
                {imagePreview && (
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="mt-2 rounded-lg max-h-48 object-cover"
                  />
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="startDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-foreground">Start Time</FormLabel>
                      <FormControl>
                        <Input
                          type="datetime-local"
                          className="h-12 bg-background/50 border-input focus:border-primary/50"
                          {...field}
                        />
                      </FormControl>
                      <p className="text-xs text-muted-foreground mt-1">Default: Now</p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="endDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-foreground">End Time</FormLabel>
                      <FormControl>
                        <Input
                          type="datetime-local"
                          className="h-12 bg-background/50 border-input focus:border-primary/50"
                          {...field}
                        />
                      </FormControl>
                      <p className="text-xs text-muted-foreground mt-1">Optional</p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <DialogFooter className="pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="w-full sm:w-auto px-8 h-12 bg-gradient-premium text-white shadow-lg font-semibold rounded-xl hover:opacity-95 dark:text-white"
                  disabled={submitting}
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : editingPost ? (
                    'Update'
                  ) : (
                    'Create'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
